<template>
    <div id="filters-box" class="flex flex-column h-full">

        <!-- Header -->
        <div class="w-full flex flex-row align-items-center" style="height: 3.95rem; padding: 1rem; background-color: var(--filters-block-header-background-color); border-top-left-radius: var(--my-border-radius); border-top-right-radius: var(--my-border-radius);">
            <span style="font-size: var(--filters-title-font-size); font-weight: var(--filters-title-font-weight); padding: 0px; margin: 0px; margin-bottom: 0;">Advanced Filters</span>
        </div>

        <!-- Content -->
        <div class="flex flex-column" style="padding: 1rem; background-color: var(--filters-block-content-background-color); border-bottom-left-radius: var(--my-border-radius); border-bottom-right-radius: var(--my-border-radius);">

            <!-- Filters -->
            <div class="filters flex flex-column" style="flex: 1; padding-top: 0px; margin-top: 0px; gap: 1.3rem">

                <!-- Owner -->
                <div class="selector">
                    <span>Owner</span>
                    <SelectButton v-model="selectionLicensesAdminCopy" :options="optionsAdmin" aria-labelledby="basic" @update:model-value="selectionLicensesAdminCopy = selectionLicensesAdminCopy ? selectionLicensesAdminCopy : 'All'; $emit('update:selection-licenses-admin', selectionLicensesAdminCopy);"/> 
                </div>  
                
                <!-- Status -->
                <div class="selector">
                    <span>Status</span>
                    <SelectButton v-model="selectionLicensesActivatedCopy" :options="optionsActivated" aria-labelledby="basic" @update:model-value="selectionLicensesActivatedCopy = selectionLicensesActivatedCopy ? selectionLicensesActivatedCopy : 'All'; $emit('update:selection-licenses-activated', selectionLicensesActivatedCopy);"/> 
                </div>  

                <!-- Application -->
                <div class="selector" >
                    <span>Application</span>
                    <Dropdown v-model="selectionLicensesApplicationCopy" :options="optionsApplicationForFiltering" filter placeholder="Select an Application" @update:model-value="$emit('update:selection-licenses-application', selectionLicensesApplicationCopy);">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex flex-row align-items-center h-full">
                                <div v-if="slotProps.value !== 'None'" class="potentially-long-label">{{ slotProps.value }}</div>
                                <div v-else class="p-disabled">{{ slotProps.placeholder }}</div>
                            </div>
                        </template>
                        <template #option="slotProps">
                            <div class="flex flex-row align-items-center">
                                <div v-if="slotProps.option && slotProps.option != 'None'">{{ slotProps.option }}</div>
                                <div v-else style="font-style: italic;">None</div>
                            </div>
                        </template>
                    </Dropdown>
                </div> 

                <!-- Active User -->
                <div class="selector" >
                    <span>Active User</span>
                    <Dropdown :disabled="!enableActiveUserDropdown" v-model="selectionLicensesActiveUserCopy" :options="optionsUsersForFiltering" filter :filterFields="['first_name', 'last_name', 'email']" placeholder="Select a User" @update:model-value="$emit('update:selection-licenses-active-user', selectionLicensesActiveUserCopy);">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex flex-row align-items-center h-full">
                                <div v-if="slotProps.value.email.length>0" class="potentially-long-label">{{ slotProps.value.first_name }} {{ slotProps.value.last_name }} ({{ slotProps.value.email }})</div>
                                <div v-else class="p-disabled">{{ slotProps.placeholder }}</div>
                            </div>
                        </template>
                        <template #option="slotProps">
                            <div class="flex flex-row align-items-center">
                                <div v-if="slotProps.option && slotProps.option.email.length>0">{{ slotProps.option.first_name }} {{ slotProps.option.last_name }} ({{ slotProps.option.email }})</div>
                                <div v-else style="font-style: italic;">None</div>
                            </div>
                        </template>
                    </Dropdown>
                </div> 

                <!-- Active Device -->
                <div class="selector">
                    <span>Active Device</span>
                    <Dropdown :disabled="!enableHardwareDropdown" v-model="selectionLicensesHardwareCopy" :options="optionsHardwaresForFiltering" filter :filterFields="['label', 'serial']" placeholder="Select a Device" @update:model-value="$emit('update:selection-licenses-hardware', selectionLicensesHardwareCopy);">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex flex-row align-items-center h-full">
                                <div v-if="slotProps.value.serial.length>0" class="potentially-long-label">{{ slotProps.value.label }} ({{ slotProps.value.serial }})</div>
                                <div v-else class="p-disabled">{{ slotProps.placeholder }}</div>
                            </div>
                        </template>
                        <template #option="slotProps">
                            <div class="flex flex-row align-items-center">
                                <div v-if="slotProps.option && slotProps.option.serial.length>0">{{ slotProps.option.label }} ({{ slotProps.option.serial }})</div>
                                <div v-else style="font-style: italic;">None</div>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>

            <!-- Reset Filters -->
            <div class="filter-button justify-self-end">
                <Button outlined class="w-full mt-5" size="small" style="margin-top: auto;" @click="reset" :disabled="numberOfFilters_updated===0">
                    <div class="flex flex-row w-full justify-content-center align-items-center gap-2">
                        <SvgFilterOff className="table-licenses-svg" color="var(--button-normal-color)"/>
                        <span>Reset filters</span>
                    </div>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, watchEffect, computed } from 'vue';
// PrimeVue
import Button from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import Dropdown from 'primevue/dropdown';
// Others
import { useStore } from 'vuex'
import LicensesUtils from '@/composables/LicensesUtils';
import UsersUtils from '@/composables/UsersUtils';
import SvgFilterOff from '@/components/SVGs/SvgFilterOff.vue';

const store = useStore()
const { allApplications, allDevices } = LicensesUtils(store)
const { allUsers } = UsersUtils(store)

// --- PROPS ---
const props = defineProps({
    selectionLicensesAdmin: String,
    selectionLicensesActivated: String,
    selectionLicensesApplication: String,
    selectionLicensesActiveUser: Object,
    selectionLicensesHardware: Object
})

const selectionLicensesAdminCopy = ref(props.selectionLicensesAdmin)
const selectionLicensesActivatedCopy = ref(props.selectionLicensesActivated)
const selectionLicensesApplicationCopy = ref(props.selectionLicensesApplication)
const selectionLicensesActiveUserCopy = ref(props.selectionLicensesActiveUser)
const selectionLicensesHardwareCopy = ref(props.selectionLicensesHardware)

// --- EMITS ---
const emit = defineEmits(['update:selection-licenses-admin', 'update:selection-licenses-activated', 'update:selection-licenses-application', 'update:selection-licenses-active-user', 'update:selection-licenses-hardware', 'update:number-of-filters'])

// --- Options ---
const optionsApplicationForFiltering = computed(() =>  allApplications.value ? ["None", ...(allApplications.value || [])] : ["None"] )
const optionsUsersForFiltering = computed(() =>  allUsers.value ? [{'id': '', 'first_name': '', 'last_name': '', 'email' : ''}, store.state.user.user, ...allUsers.value || []] : [{'id': '', 'first_name': '', 'last_name': '', 'email' : ''}])
const optionsHardwaresForFiltering = computed(() =>  allDevices.value ? [{'label': '', 'serial' : ''}, ...allDevices.value || []] : [{'label': '', 'serial' : ''}] )
const optionsAdmin = ref(['All', 'Me', 'Others']);
const optionsActivated = ref(['All', 'Available', 'In Use']);


// --- Track of number of filters ON ---
const numberOfFilters_updated = computed(() => {
    let count = 0
    if (selectionLicensesAdminCopy.value !== "All") { count++; }
    if (selectionLicensesActivatedCopy.value !== "All") { count++; }
    if (selectionLicensesApplicationCopy.value !== "None") { count++; }
    if (selectionLicensesActiveUserCopy.value.email.length>0) { count++; }
    if (selectionLicensesHardwareCopy.value.serial.length>0) { count++; }
    return count
})
watchEffect(() => {
    emit('update:number-of-filters', numberOfFilters_updated.value)
})

// --- Enable/Disable : When 'Available' only, we shouldn't be able to set an 'Active User' or 'Active Device' option ---
const enableActiveUserDropdown = ref(true)
const enableHardwareDropdown = ref(true)

watchEffect(() => {
  enableActiveUserDropdown.value = selectionLicensesActivatedCopy.value !== 'Available'
  if (!enableActiveUserDropdown.value) { selectionLicensesActiveUserCopy.value = {'first_name': '', 'last_name': '', 'email': ''}; emit('update:selection-licenses-active-user', selectionLicensesActiveUserCopy.value) }
  enableHardwareDropdown.value = selectionLicensesActivatedCopy.value !== 'Available'
  if (!enableHardwareDropdown.value) { selectionLicensesHardwareCopy.value = {'label': '', 'serial': ''}; emit('update:selection-licenses-hardware', selectionLicensesHardwareCopy.value) }
})

// --- Reset button clicked ---
const reset = () => {
    selectionLicensesAdminCopy.value = "All"; emit('update:selection-licenses-admin', selectionLicensesAdminCopy.value)
    selectionLicensesActivatedCopy.value = "All"; emit('update:selection-licenses-activated', selectionLicensesActivatedCopy.value)
    selectionLicensesApplicationCopy.value = "None"; emit('update:selection-licenses-application', selectionLicensesApplicationCopy.value)
    selectionLicensesActiveUserCopy.value = {'first_name': '', 'last_name': '', 'email': ''}; emit('update:selection-licenses-active-user', selectionLicensesActiveUserCopy.value)
    selectionLicensesHardwareCopy.value = {'label': '', 'serial': ''}; emit('update:selection-licenses-hardware', selectionLicensesHardwareCopy.value)
}
</script>

<style scoped>
#filters-box {
    color: var(--theme-font-color);
}
/* Filter blocks */
.filters .selector {
    display: flex;
    flex-direction: column;
    gap: 2.5px;
}
/* Selectors filters */
.filters .selector :deep(.p-selectbutton) {
    width: 100%;
    display: flex;
    flex-direction: row;
}
.filters .selector :deep(.p-button .p-button-label) {
    font-size: var(--inputcomponent-normal-font-size);
    font-weight: var(--inputcomponent-font-weight);
}
/* Dropdown filters */
.filters .selector :deep(.p-dropdown) {
    width: 100%;
}
/* Selectors/Dropdown common style */
.filters .selector :deep(.p-selectbutton), :deep(.p-dropdown) {
    height: 2.1rem;
}
.filters .selector :deep(.p-button-label:not(.p-highlight .p-button-label)), .filters .selector :deep(.p-dropdown-label), .filters .selector :deep(.p-dropdown-item-label:not(.p-highlight .p-dropdown-item-label)), .filters .selector :deep(.p-icon) {
    color: var(--theme-font-color);
    opacity: 100%;
}
.filters .selector span {
    font-size: var(--filters-subtitle-font-size);
    font-weight: var(--filters-subtitle-font-weight);
}
</style>